import * as Sentry from '@sentry/react';
import sortBy from 'lodash/sortBy';
import { ReactNode } from 'react';
import { CompiledPortfolio, SerializedUser, Subscription } from '../types';

import { UserStatus } from '../config/constants';

const STATUS_PRIORITY: Record<Subscription['status'], number> = {
  active: 0,
  paused: 1,
  past_due: 2,
  lapsed: 3,
  cancelled: 3,
};

export function getLatestSubscription(user: SerializedUser) {
  if (user.subscriptions.length === 0) {
    return undefined;
  }

  const nonTerminal = user.subscriptions.filter((s) => !isTerminal(s.status));
  if (nonTerminal.length > 1) {
    Sentry.captureMessage(
      `User ${user.userId} has ${nonTerminal.length} non-terminal subscriptions`,
      'warning'
    );
  }

  const sorted = sortBy(user.subscriptions, [
    (s: Subscription) => STATUS_PRIORITY[s.status],
    (s: Subscription) => -new Date(s.createdAt).getTime(),
  ]);

  return sorted[0] as Subscription | undefined;
}

export function getLatestLapsedSubscription(user: SerializedUser) {
  const latestLapsedSubscription = sortBy(user.subscriptions, 'createdAt')
    .reverse()
    .find((subscription) => subscription.status === 'lapsed');

  return latestLapsedSubscription;
}

export interface Plan {
  name: string;
  offsetPercentage: number;
  description: ReactNode;
  extendedDescription?: string;
  isPopular?: boolean;
  amountInUSDCents?: number;
  amountInUserCurrency?: number;
}

export function getLowerCostPlans(isHousehold: boolean): Plan[] {
  return [
    {
      name: 'Climate starter',
      offsetPercentage: 0.5,
      description: (
        <span>
          Offsets <b>50%</b> of your {isHousehold ? 'household’s' : ''}{' '}
          emissions.
        </span>
      ),
    },
    {
      name: 'Climate steward',
      offsetPercentage: 1,
      isPopular: true,
      description: (
        <span>
          Offsets <b>100%</b> of your {isHousehold ? 'household’s' : ''}{' '}
          emissions.
        </span>
      ),
    },
    {
      name: 'Climate hero',
      offsetPercentage: 2,
      description: (
        <span>
          Offsets <b>200%</b> of your {isHousehold ? 'household’s' : ''}{' '}
          emissions.
        </span>
      ),
    },
  ];
}

export function getDefaultPlans(
  portfolio: CompiledPortfolio,
  isHousehold: boolean
): Plan[] {
  return [
    {
      name: 'Climate neutral',
      offsetPercentage: 1,
      description: (
        <span>
          Offsets <b>100%</b> of your {isHousehold ? 'household’s' : ''}{' '}
          emissions.
        </span>
      ),
    },
    {
      name: 'Climate steward',
      offsetPercentage: 2,
      isPopular: true,
      description: (
        <span>
          Offsets <b>200%</b> of your {isHousehold ? 'household’s' : ''}{' '}
          emissions.
        </span>
      ),
    },
    {
      name: 'Climate hero',
      offsetPercentage: 3,
      description: (
        <span>
          Offsets <b>300%</b> of your {isHousehold ? 'household’s' : ''}{' '}
          emissions.
        </span>
      ),
    },
  ];
}

export function isDelinquent(userStatus: SerializedUser['status']) {
  switch (userStatus) {
    case UserStatus.LAPSED:
    case UserStatus.PAST_DUE:
      return true;
    default:
      return false;
  }
}

export function isDelinquentSubscriptionStatus(
  subscriptionStatus: Subscription['status']
) {
  switch (subscriptionStatus) {
    case 'past_due':
    case 'lapsed':
      return true;
    default:
      return false;
  }
}

export function isPastDueRecoveryOwnerDelinquent(
  user: SerializedUser,
  recoveryTeamId: string | null
) {
  if (!recoveryTeamId) return isDelinquent(user.status);

  const recoveryTeam = [user.team, ...(user.ownedTeams ?? [])].find(
    (team) => team?.id === recoveryTeamId
  );
  if (!recoveryTeam) return true;

  return (
    recoveryTeam.subscriptionStatus != null &&
    isDelinquentSubscriptionStatus(recoveryTeam.subscriptionStatus)
  );
}

export function isTerminal(status: Subscription['status']) {
  switch (status) {
    case 'cancelled':
    case 'lapsed':
      return true;
    case 'active':
    case 'past_due':
    case 'paused':
      return false;
    default:
      // Will not compile if someone adds a new status
      return ((unexpectedStatus: never) => {
        throw `unexpected subscription status: ${unexpectedStatus}`;
      })(status);
  }
}

export function isWinbackUser(user: SerializedUser) {
  return user.subscriptions.some((s) => isTerminal(s.status));
}
